import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {NavigationEnd, Router, RouterLink, RouterLinkActive} from '@angular/router';
import {
  IonApp,
  IonButton,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonMenu,
  IonMenuToggle,
  IonModal,
  IonRouterLink,
  IonRouterOutlet,
  IonSplitPane,
} from '@ionic/angular/standalone';
import {addIcons} from 'ionicons';
import {
  archiveOutline,
  archiveSharp,
  bookmarkOutline,
  bookmarkSharp,
  close,
  heartOutline,
  heartSharp,
  mailOutline,
  mailSharp,
  paperPlaneOutline,
  paperPlaneSharp,
  trashOutline,
  trashSharp,
  warningOutline,
  warningSharp
} from 'ionicons/icons';
import {distinct, filter, lastValueFrom, Subscription} from 'rxjs';
import {AuthService} from './core/services/auth.service';
import {ProjectService} from './core/services/project.service';
import {UserService} from './core/services/user.service';
import {ErrorService} from './core/services/error.service';
import {DatePipe, NgIf} from '@angular/common';
import {SharedService} from './core/services/shared.service';
import {MapPreviewComponent} from './core/components/map-preview/map-preview.component';
import {ApiUtilService} from './core/services/api-util.service';
import {isFuture, isPast, parse} from 'date-fns';
import {environment} from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [RouterLinkActive, IonApp, IonSplitPane, IonMenu, IonContent, IonList, IonListHeader, IonMenuToggle, IonItem, IonIcon, IonLabel, IonRouterLink, IonRouterOutlet, RouterLink, DatePipe, IonButton, IonModal, MapPreviewComponent, NgIf],
})
export class AppComponent implements OnInit, OnDestroy {
  public isDisableSideMenu = true;
  public appPages = [
    {title: 'HOME', url: '/editor/home', icon: 'home'},
    {title: 'スポット管理', url: '/editor/spot', icon: 'spot'},
    {title: '広告登録', url: '/editor/advertisement', icon: 'advertisement'},
    {title: 'ユーザー管理', url: '/editor/user', icon: 'person'},
  ];
  public isPreviewModalOpen = false;
  public isPublicProject = false;
  public isOpenPrivateMessageModal = false;

  authService = inject(AuthService);
  projectService = inject(ProjectService);
  userService = inject(UserService);
  errorService = inject(ErrorService);
  sharedService = inject(SharedService);
  apiUtilService = inject(ApiUtilService);

  routerSubscription!: Subscription;
  authStateSubscription!: Subscription;

  constructor(private router: Router) {
    addIcons({
      mailOutline,
      mailSharp,
      paperPlaneOutline,
      paperPlaneSharp,
      heartOutline,
      heartSharp,
      archiveOutline,
      archiveSharp,
      trashOutline,
      trashSharp,
      warningOutline,
      warningSharp,
      bookmarkOutline,
      bookmarkSharp,
      close
    });
  }

  ngOnInit() {
    this.watchRouter();
    this.watchAuthState();
    if (environment?.envName) {
      console.log('envName', environment.envName);
    }
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
    this.authStateSubscription?.unsubscribe();
  }

  async togglePublic() {
    if (this.projectService?.currentProject?.publicSetting) {
      this.isOpenPrivateMessageModal = true;
      return;
    }
    this.isPreviewModalOpen = true;
  }

  watchRouter() {
    this.routerSubscription = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
      // URLが変わるたびに動く処理
      this.isDisableSideMenu = !event?.urlAfterRedirects?.includes('editor');
    });
  }

  watchAuthState() {
    this.authStateSubscription = this.authService.authState$.pipe(distinct()).subscribe(async (isSignIn) => {
      if (isSignIn) {
        //   ログイン後の処理
        this.apiUtilService.token = await this.authService.getJwtToken();
        const projectName = await this.authService.getUserProjectName();
        const project$ = this.projectService.getProjectByName(projectName);
        lastValueFrom(await project$).then((data) => {
          this.projectService.currentProject = data?.project;
        }).catch(error => this.errorService.onError({message: 'Get Project Error', error}));
        const currentUserData = await this.authService.getCurrentUser();
        this.authService.getUserPermission().then((data) => {
          this.userService.currentUser = {
            name: currentUserData.username,
            projectName,
            permission: data || undefined
          };
        });
      }
    });
  }

  previewPcView() {
    const url = new URL(window.location.href);
    const origin = url.origin;
    window.open(`${origin}/editor/preview`, '_blank');
  }

  async makePrivate() {
    const loading = await this.sharedService.presentLoading();
    await loading.present();
    const project = {...this.projectService.currentProject};
    const body = {projectName: project.projectName, project};
    project.publicSetting = false;
    const update$ = this.projectService.updateProject(body);
    lastValueFrom(await update$).then(() => {
      this.projectService.currentProject = project;
      this.isOpenPrivateMessageModal = false;
    }).catch(error => this.errorService.onError({
      message: 'Update Project Error',
      error
    })).finally(() => loading.dismiss());
  }

  async publicProject() {
    if (this.projectService?.currentProject?.publicSetting) return;
    const loading = await this.sharedService.presentLoading();
    await loading.present();
    const project = {...this.projectService.currentProject};
    const body = {projectName: project.projectName, project};
    project.publicSetting = true;
    const update$ = this.projectService.updateProject(body);
    lastValueFrom(await update$).then(() => {
      this.projectService.currentProject = project;
      this.isPreviewModalOpen = false;
    }).catch(error => this.errorService.onError({
      message: 'Update Project Error',
      error
    })).finally(() => loading.dismiss());
  }

  get isPublicPeriod() {
    const start = parse(this.projectService.currentProject?.startDate, "yyyy-MM-dd'T'HH:mm:ssXXX", new Date());
    const end = parse(this.projectService.currentProject?.endDate, "yyyy-MM-dd'T'HH:mm:ssXXX", new Date());
    return isPast(start) && isFuture(end);
  }
}
